import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppState } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  Alert,
} from '../components/rn';
import ActivityIndicator from '../components/LoadingIndicator';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing, fontSize, ThemeColors, radius } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import CompactActionButton from '../components/CompactActionButton';
import { MIN_TOUCH_TARGET } from '../components/CompactActionButton';
import { useTranslation } from '../i18n/I18nContext';
import { formatDisplayCount } from '../i18n/formatCount';
import { api, ApiError, Meetup } from '../services/api';
import { formatMoney } from '../data/currencies';
import Avatar from '../components/Avatar';
import NotificationBellButton from '../components/NotificationBellButton';
import {
  restoreAcceptedMeetup,
  restoreAcceptedMeetupTime,
  syncAcceptedMeetup,
} from '../calendar/acceptedMeetupSync';
import {
  readMeetupSystemCalendarReconciliation,
} from '../calendar/systemCalendar';
import type { SystemCalendarReconciliation } from '../calendar/systemCalendarTypes';
import {
  eventOverlapsDay,
  externalCalendarEventVisibilityKey,
  ExternalDeviceCalendarEvent,
  intervalOverlapsDay,
} from '../calendar/deviceCalendarsTypes';
import {
  loadSelectedDeviceCalendarEventsForRange,
  loadSelectedGoogleCalendarEventsForRange,
  mergeExternalCalendarRefresh,
} from '../calendar/selectedCalendarEvents';
import { storage } from '../services/storage';
import { reservationStatusKey } from '../calendar/reservationStatus';
import { weekContaining, weekStartTimestamp } from '../calendar/week';
import { getDeviceFirstWeekday, getDeviceTimeZone } from '../utils/locale';
import {
  formatExternalCalendarEventWhen,
  formatNosubjectCalendarEventWhen,
} from '../calendar/eventPresentation';
import {
  canProposeReservationReschedule,
} from '../calendar/reservationActions';

type Props = {
  token: string;
  myId: number;
  onNotificationCenter: () => void;
  onOpenReservation: (reservationId: number) => void;
};

type ViewMode = 'requests' | 'grid' | 'history';

// "İstekler" sekmesindeki öncelik sırası — Airbnb host-inbox mantığı: senden
// eylem bekleyen (pending + received) her zaman en üstte, sonra yanıt beklenen
// gönderilmiş teklifler, sonra onaylanmış yaklaşan buluşmalar, en altta kapanmış
// (reddedilen/iptal edilen) — kapanmışlar zaten soluk (rowClosed) render ediliyor.
function priorityRank(item: Meetup): number {
  if (item.status === 'pending' && item.direction === 'received') return 0;
  if (item.status === 'pending') return 1;
  if (item.status === 'accepted') return 2;
  return 3;
}

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function dateKey(date: Date) {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function meetupOverlapsDay(meetup: Meetup, day: Date): boolean {
  const start = new Date(meetup.proposed_at);
  const end = new Date(start.getTime() + Math.max(1, meetup.duration_minutes) * 60_000);
  return intervalOverlapsDay(start, end, day);
}

export default function CalendarScreen({ token, myId, onNotificationCenter, onOpenReservation }: Props) {
  const insets = useSafeAreaInsets();
  const colors = useTheme();
  const { t, lang } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const firstWeekday = useMemo(() => getDeviceFirstWeekday(), []);
  const deviceTimeZone = useMemo(() => getDeviceTimeZone() ?? 'UTC', []);
  const [meetups, setMeetups] = useState<Meetup[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const visibleWeekStart = useMemo(
    () => weekStartTimestamp(selectedDate, firstWeekday),
    [firstWeekday, selectedDate]
  );
  const [externalEvents, setExternalEvents] = useState<ExternalDeviceCalendarEvent[]>([]);
  const [externalLoading, setExternalLoading] = useState(false);
  const [externalFailed, setExternalFailed] = useState(false);
  const [externalReloadKey, setExternalReloadKey] = useState(0);
  const [historyMeetups, setHistoryMeetups] = useState<Meetup[]>([]);
  const [historyCursor, setHistoryCursor] = useState<string | null>(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFailed, setHistoryFailed] = useState(false);
  // Haftalar hızla değiştiğinde daha eski ağ yanıtı yeni haftanın sonucunu
  // ezemez. Aynı sayaç sessiz mutasyon-sonrası uzlaştırmaları da kapsar.
  const loadRequestRef = useRef(0);
  const hasLoadedRef = useRef(false);
  const historyRequestRef = useRef(0);
  const [hiddenExternalEventKeys, setHiddenExternalEventKeys] = useState<Set<string>>(
    () => new Set()
  );
  const [hiddenExternalEventsLoaded, setHiddenExternalEventsLoaded] = useState(false);
  const [calendarReconciliations, setCalendarReconciliations] = useState<
    Map<number, SystemCalendarReconciliation>
  >(
    () => new Map()
  );

  const load = useCallback(async (showLoading = true) => {
    const requestId = ++loadRequestRef.current;
    if (showLoading) setLoading(true);
    setFailed(false);
    try {
      const rangeStart = new Date(visibleWeekStart);
      const rangeEnd = new Date(
        rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate() + 7
      );
      const [requests, calendar] = await Promise.all([
        api.getMeetupsPage(token, { status: 'pending', limit: 100 }),
        api.getMeetupsPage(token, {
          status: 'accepted',
          from: rangeStart.toISOString(),
          to: rangeEnd.toISOString(),
          limit: 100,
        }),
      ]);
      if (requestId !== loadRequestRef.current) return false;
      const byId = new Map<number, Meetup>();
      for (const meetup of [...requests.items, ...calendar.items]) byId.set(meetup.id, meetup);
      setMeetups([...byId.values()]);
      return true;
    } catch {
      if (requestId === loadRequestRef.current) setFailed(true);
      return false;
    } finally {
      if (requestId === loadRequestRef.current) {
        hasLoadedRef.current = true;
        setLoading(false);
      }
    }
  }, [token, visibleWeekStart]);

  const loadHistory = useCallback(async (cursor?: string, replace = false) => {
    const requestId = ++historyRequestRef.current;
    setHistoryLoading(true);
    setHistoryFailed(false);
    try {
      const page = await api.getMeetupsPage(token, {
        scope: 'history',
        limit: 50,
        ...(cursor ? { cursor } : {}),
      });
      if (requestId !== historyRequestRef.current) return;
      setHistoryMeetups((current) => {
        const byId = new Map<number, Meetup>();
        for (const meetup of replace ? [] : current) byId.set(meetup.id, meetup);
        for (const meetup of page.items) byId.set(meetup.id, meetup);
        return [...byId.values()].sort(
          (a, b) => {
            const aTime = new Date(a.proposed_at).getTime();
            const bTime = new Date(b.proposed_at).getTime();
            if (aTime !== bTime) return bTime - aTime;
            return b.id - a.id;
          },
        );
      });
      setHistoryCursor(page.next_cursor);
      setHistoryLoaded(true);
    } catch {
      if (requestId === historyRequestRef.current) setHistoryFailed(true);
    } finally {
      if (requestId === historyRequestRef.current) setHistoryLoading(false);
    }
  }, [token]);

  // İlk açılış tam yükleme yüzeyini kullanır. Sonraki hafta geçişlerinde mevcut
  // takvim yerinde kalır; kullanıcı hızlıca başka haftaya geçebilir ve yalnız en
  // son isteğin sonucu uygulanır.
  useEffect(() => { load(!hasLoadedRef.current); }, [load]);

  useEffect(() => {
    if (viewMode === 'history' && !historyLoaded) loadHistory(undefined, true);
  }, [historyLoaded, loadHistory, viewMode]);

  useEffect(() => {
    let active = true;
    storage.getCalendarViewMode(myId).then((saved) => {
      if (active && saved) setViewMode(saved);
    }).catch(() => {});
    return () => { active = false; };
  }, [myId]);

  useEffect(() => {
    let active = true;
    setHiddenExternalEventsLoaded(false);
    storage.getHiddenExternalCalendarEventKeys(myId)
      .then((keys) => {
        if (active) setHiddenExternalEventKeys(new Set(keys));
      })
      .catch(() => {
        if (active) setHiddenExternalEventKeys(new Set());
      })
      .finally(() => {
        if (active) setHiddenExternalEventsLoaded(true);
      });
    return () => { active = false; };
  }, [myId]);

  useEffect(() => {
    let active = true;
    const futureAccepted = meetups.filter((meetup) => {
      if (meetup.status !== 'accepted') return false;
      const endAt = new Date(meetup.proposed_at).getTime()
        + Math.max(1, meetup.duration_minutes) * 60_000;
      return Number.isFinite(endAt) && endAt > Date.now();
    });
    Promise.all(futureAccepted.map(async (meetup) => {
      await syncAcceptedMeetup(myId, meetup, {
        meetupTitle: (name) => t('calendar.systemEventMeetupTitle', { name }),
        callTitle: (name) => t('calendar.systemEventCallTitle', { name }),
        notes: t('calendar.systemEventNotes'),
      });
      return {
        id: meetup.id,
        reconciliation: await readMeetupSystemCalendarReconciliation(myId, meetup.id),
      };
    })).then((results) => {
      if (!active) return;
      setCalendarReconciliations(new Map(
        results.flatMap(({ id, reconciliation }) =>
          reconciliation ? [[id, reconciliation] as const] : []
        )
      ));
    }).catch(() => {});
    return () => { active = false; };
  }, [meetups, myId, t]);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', (state) => {
      if (state !== 'active') return;
      load();
      setExternalReloadKey((key) => key + 1);
    });
    return () => subscription.remove();
  }, [load]);

  function statusLabel(status: Meetup['status']) {
    return t(reservationStatusKey(status));
  }

  function reservationActionError(
    error: unknown,
    fallbackKey: string = 'calendar.actionFailed',
  ): string {
    if (error instanceof ApiError) {
      if (error.code === 'RESERVATION_REVISION_CONFLICT') {
        return t('calendar.reservationChanged');
      }
      if (error.code === 'RESERVATION_TIME_UNAVAILABLE') {
        return t('calendar.reservationUnavailable');
      }
      if (error.code === 'RESERVATION_START_IN_PAST') {
        return t('calendar.reservationPast');
      }
      if (error.code === 'RESERVATION_EXECUTION_LOCKED') {
        return t('calendar.reservationExecutionLocked');
      }
    }
    return t(fallbackKey);
  }

  async function restoreCalendarCopy(item: Meetup) {
    setBusyId(item.id);
    try {
      const result = await restoreAcceptedMeetup(myId, item, {
        meetupTitle: (name) => t('calendar.systemEventMeetupTitle', { name }),
        callTitle: (name) => t('calendar.systemEventCallTitle', { name }),
        notes: t('calendar.systemEventNotes'),
      });
      if (result !== 'created' && result !== 'existing') {
        Alert.alert(t('common.error'), t('calendar.systemCalendarRestoreFailed'));
        return;
      }
      setCalendarReconciliations((current) => {
        const next = new Map(current);
        next.delete(item.id);
        return next;
      });
    } catch {
      Alert.alert(t('common.error'), t('calendar.systemCalendarRestoreFailed'));
    } finally {
      setBusyId(null);
    }
  }

  function formatWhen(iso: string) {
    const d = new Date(iso);
    return `${d.toLocaleDateString(lang, { day: 'numeric', month: 'short' })} · ${d.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })}`;
  }

  function formatRange(startIso: string, endIso: string) {
    const start = new Date(startIso);
    const end = new Date(endIso);
    if (!sameDay(start, end)) return `${formatWhen(startIso)} – ${formatWhen(endIso)}`;
    return `${formatWhen(startIso)} – ${end.toLocaleTimeString(lang, {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  }

  async function restoreCalendarTime(item: Meetup) {
    setBusyId(item.id);
    try {
      const result = await restoreAcceptedMeetupTime(myId, item, {
        meetupTitle: (name) => t('calendar.systemEventMeetupTitle', { name }),
        callTitle: (name) => t('calendar.systemEventCallTitle', { name }),
        notes: t('calendar.systemEventNotes'),
      });
      if (result !== 'existing' && result !== 'created') {
        const reconciliation = await readMeetupSystemCalendarReconciliation(myId, item.id);
        if (reconciliation) {
          setCalendarReconciliations((current) => new Map(current).set(item.id, reconciliation));
        }
        Alert.alert(t('common.error'), t('calendar.systemCalendarTimeRestoreFailed'));
        return;
      }
      setCalendarReconciliations((current) => {
        const next = new Map(current);
        next.delete(item.id);
        return next;
      });
    } catch {
      Alert.alert(t('common.error'), t('calendar.systemCalendarTimeRestoreFailed'));
    } finally {
      setBusyId(null);
    }
  }

  async function proposeCalendarTime(
    item: Meetup,
    reconciliation: Extract<SystemCalendarReconciliation, { state: 'externally-changed' }>
  ) {
    setBusyId(item.id);
    try {
      await api.proposeMeetupReschedule(
        token,
        item.id,
        reconciliation.observedStartDate,
        item.record_revision,
      );
      Alert.alert(
        t('calendar.rescheduleSent'),
        t('calendar.rescheduleSentBody', { name: item.other_name })
      );
      await load(false);
    } catch (error) {
      // Yeni saat POST yanıtı kaybolmuş olabilir; aynı eski liste satırını
      // yeniden eyleme açmadan önce kanonik haftayı uzlaştır.
      await load(false);
      Alert.alert(
        t('common.error'),
        reservationActionError(error, 'calendar.rescheduleFailed'),
      );
    } finally {
      setBusyId(null);
    }
  }

  async function reviewCalendarTime(item: Meetup, reconciliation: SystemCalendarReconciliation) {
    if (reconciliation.state !== 'externally-changed') return;
    setBusyId(item.id);
    let canonical: Meetup;
    try {
      // Takvim listesi çağrı icra kanıtını bilmez. Karar penceresini açmadan
      // kanonik ayrıntıyı okuyarak başlamış/tamamlanmış çağrıya artık mümkün
      // olmayan bir yeni-saat eylemi sunmayız.
      canonical = await api.getMeetup(token, item.id);
    } catch {
      Alert.alert(t('common.error'), t('calendar.actionFailed'));
      return;
    } finally {
      setBusyId(null);
    }
    const canonicalStart = new Date(canonical.proposed_at).getTime();
    const canonicalEnd = new Date(
      canonicalStart + Math.max(1, canonical.duration_minutes) * 60_000
    );
    const canRestore = canonical.status === 'accepted';
    const serverNowMs = canonical.server_now
      ? new Date(canonical.server_now).getTime()
      : Date.now();
    const observedStartMs = new Date(reconciliation.observedStartDate).getTime();
    const canPropose = canProposeReservationReschedule(canonical, serverNowMs)
      && observedStartMs > serverNowMs;
    Alert.alert(
      t('calendar.systemCalendarChangeTitle'),
      t('calendar.systemCalendarChangeBody', {
        nosubject: formatRange(canonical.proposed_at, canonicalEnd.toISOString()),
        system: formatRange(
          reconciliation.observedStartDate,
          reconciliation.observedEndDate
        ),
      }),
      [
        { text: t('common.done'), style: 'cancel' },
        ...(canRestore ? [{
          text: t('calendar.restoreNosubjectTime'),
          onPress: () => { restoreCalendarTime(canonical); },
        }] : []),
        ...(canPropose ? [{
          text: t('calendar.proposeReschedule'),
          onPress: () => { proposeCalendarTime(canonical, reconciliation); },
        }] : []),
      ]
    );
  }

  const sortedForRequests = useMemo(() => {
    return [...meetups].sort((a, b) => {
      const r = priorityRank(a) - priorityRank(b);
      if (r !== 0) return r;
      return new Date(a.proposed_at).getTime() - new Date(b.proposed_at).getTime();
    });
  }, [meetups]);

  // Tek satırlık hafta cihazın bölgesel takvim ayarına göre başlar.
  const weekDays = useMemo(
    () => weekContaining(new Date(visibleWeekStart), firstWeekday),
    [firstWeekday, visibleWeekStart]
  );

  useEffect(() => {
    let active = true;
    if (viewMode !== 'grid') return () => { active = false; };
    setExternalLoading(true);
    setExternalFailed(false);
    (async () => {
      const weekStart = weekDays[0];
      const rangeStart = new Date(
        weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate()
      );
      const rangeEnd = new Date(
        rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate() + 7
      );
      const [deviceResult, googleResult] = await Promise.allSettled([
        loadSelectedDeviceCalendarEventsForRange(myId, rangeStart, rangeEnd),
        loadSelectedGoogleCalendarEventsForRange(token, rangeStart, rangeEnd),
      ]);
      if (!active) return;
      setExternalEvents((current) => mergeExternalCalendarRefresh(
        current,
        deviceResult.status === 'fulfilled' ? deviceResult.value : null,
        googleResult.status === 'fulfilled' ? googleResult.value.events : null
      ));
      setExternalFailed(
        deviceResult.status === 'rejected'
          || googleResult.status === 'rejected'
          || (googleResult.status === 'fulfilled' && googleResult.value.refreshFailed)
      );
    })()
      .catch(() => {
        if (active) {
          setExternalEvents([]);
          setExternalFailed(true);
        }
      })
      .finally(() => {
        if (active) setExternalLoading(false);
      });
    return () => { active = false; };
  }, [externalReloadKey, myId, token, viewMode, weekDays]);

  const weekdayLabels = useMemo(
    () => weekDays.map((day) => day.toLocaleDateString(lang, { weekday: 'short' })),
    [lang, weekDays]
  );

  // Takvim, karar verilmiş zaman planını gösterir. Bekleyen/reddedilen/iptal
  // edilen teklifler yönetim için yalnız "İstekler" sekmesinde kalır.
  const acceptedMeetups = useMemo(
    () => meetups.filter((meetup) => meetup.status === 'accepted'),
    [meetups]
  );

  const meetupsByDate = useMemo(() => {
    const map = new Map<string, Meetup[]>();
    for (const m of acceptedMeetups) {
      for (const day of weekDays) {
        if (!meetupOverlapsDay(m, day)) continue;
        const key = dateKey(day);
        const list = map.get(key) ?? [];
        list.push(m);
        map.set(key, list);
      }
    }
    return map;
  }, [acceptedMeetups, weekDays]);

  const nosubjectAgendaItems = useMemo(() => {
    return acceptedMeetups
      .filter((m) => meetupOverlapsDay(m, selectedDate))
      .sort((a, b) => new Date(a.proposed_at).getTime() - new Date(b.proposed_at).getTime());
  }, [acceptedMeetups, selectedDate]);

  const visibleExternalEvents = useMemo(
    () => hiddenExternalEventsLoaded
      ? externalEvents.filter((event) =>
          !hiddenExternalEventKeys.has(externalCalendarEventVisibilityKey(event)))
      : [],
    [externalEvents, hiddenExternalEventKeys, hiddenExternalEventsLoaded]
  );

  const externalAgendaItems = useMemo(
    () => visibleExternalEvents.filter((event) => eventOverlapsDay(event, selectedDate)),
    [selectedDate, visibleExternalEvents]
  );

  const agendaItems = useMemo(() => [
    ...nosubjectAgendaItems.map((meetup) => ({
      type: 'meetup' as const,
      key: `meetup:${meetup.id}`,
      startsAt: new Date(meetup.proposed_at).getTime(),
      meetup,
    })),
    ...externalAgendaItems.map((event) => ({
      type: 'external' as const,
      key: `external:${event.calendarId}:${event.id}:${event.startDate.toISOString()}`,
      startsAt: event.startDate.getTime(),
      event,
    })),
  ].sort((a, b) => a.startsAt - b.startsAt), [externalAgendaItems, nosubjectAgendaItems]);

  function goWeek(delta: number) {
    setSelectedDate((date) =>
      new Date(date.getFullYear(), date.getMonth(), date.getDate() + delta * 7)
    );
  }

  function selectViewMode(mode: ViewMode) {
    setViewMode(mode);
    storage.setCalendarViewMode(myId, mode).catch(() => {});
  }

  function renderRow(item: Meetup, allowActions = true) {
    const busy = busyId === item.id;
    const closed = item.status === 'declined' || item.status === 'cancelled' || item.status === 'expired';
    let frozenTotal: number | null = Number(item.total_amount);
    if (item.total_amount == null) {
      frozenTotal = item.pricing_frozen === false
        ? null
        : Number(item.recipient_price ?? item.recipient_price_time) * item.duration_minutes;
    }
    const reconciliation = calendarReconciliations.get(item.id);
    const calendarCopyRemoved = reconciliation?.state === 'externally-removed';
    const calendarCopyChanged = reconciliation?.state === 'externally-changed';
    const pendingReschedule = item.pending_reschedule;
    return (
      <View key={item.id} style={[styles.row, closed && styles.rowClosed]}>
        <TouchableOpacity
          style={styles.rowLink}
          accessibilityRole="button"
          accessibilityLabel={t('calendar.openReservation', { name: item.other_name })}
          testID={`calendar-reservation-${item.id}`}
          onPress={() => onOpenReservation(item.id)}>
          <Avatar photoUrl={item.other_photo} name={item.other_name} size={40} style={styles.avatar} />
          <View style={styles.rowText}>
            <Text style={styles.rowName}>{item.other_name}</Text>
            <Text style={styles.rowKind}>
              {t(item.kind === 'call' ? 'call.reservationLabel' : 'calendar.meetupLabel')}
            </Text>
            <Text style={styles.rowWhen}>{formatNosubjectCalendarEventWhen({
              proposedAt: item.proposed_at,
              durationMinutes: item.duration_minutes,
              sourceTimeZone: item.source_time_zone,
            }, lang, deviceTimeZone)}</Text>
            {!!item.location && <Text style={styles.rowLocation}>{item.location}</Text>}
            <Text style={styles.rowLocation}>
              {formatDisplayCount(item.duration_minutes, lang)} {t('calendar.minutesShort')}
              {frozenTotal === null ? '' : ` · ${formatMoney(
                frozenTotal,
                item.price_currency ?? item.recipient_currency
              )}`}
            </Text>
            <Text style={styles.rowStatus}>{statusLabel(item.status)}</Text>
            {pendingReschedule && (
              <Text style={styles.calendarCopyWarning}>
                {t(pendingReschedule.direction === 'received'
                  ? 'calendar.reschedulePendingReceived'
                  : 'calendar.reschedulePendingSent', {
                  time: formatNosubjectCalendarEventWhen({
                    proposedAt: pendingReschedule.proposed_at,
                    durationMinutes: item.duration_minutes,
                    sourceTimeZone: pendingReschedule.source_time_zone,
                  }, lang, deviceTimeZone),
                })}
              </Text>
            )}
            {calendarCopyRemoved && (
              <Text style={styles.calendarCopyWarning}>
                {t('calendar.systemCalendarCopyRemoved')}
              </Text>
            )}
            {calendarCopyChanged && (
              <Text style={styles.calendarCopyWarning}>
                {t('calendar.systemCalendarCopyChanged')}
              </Text>
            )}
          </View>
        </TouchableOpacity>
        {!allowActions ? null : busy ? (
          <ActivityIndicator color={colors.black} />
        ) : item.status === 'accepted' && (calendarCopyRemoved || calendarCopyChanged) ? (
          <CompactActionButton
            label={t(calendarCopyRemoved
              ? 'calendar.restoreSystemCalendarCopy'
              : 'calendar.reviewSystemCalendarChange')}
            onPress={() => calendarCopyRemoved
              ? restoreCalendarCopy(item)
              : reconciliation && reviewCalendarTime(item, reconciliation)}
            style={styles.rowAction}
            testID="calendar-row-action"
          />
        ) : null}
      </View>
    );
  }

  function externalWhen(event: ExternalDeviceCalendarEvent) {
    return formatExternalCalendarEventWhen(
      event,
      lang,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      t('calendar.allDay')
    );
  }

  function externalAvailability(event: ExternalDeviceCalendarEvent) {
    if (event.responseStatus === 'declined') return t('calendar.statusDeclined');
    if (event.availability === 'free') return t('calendar.availabilityFree');
    if (event.availability === 'tentative') return t('calendar.availabilityTentative');
    if (event.availability === 'unavailable') return t('calendar.availabilityUnavailable');
    return t('calendar.availabilityBusy');
  }

  async function hideExternalEvent(event: ExternalDeviceCalendarEvent) {
    const eventKey = externalCalendarEventVisibilityKey(event);
    setHiddenExternalEventKeys((current) => new Set(current).add(eventKey));
    try {
      await storage.hideExternalCalendarEvent(myId, eventKey);
    } catch {
      setHiddenExternalEventKeys((current) => {
        const next = new Set(current);
        next.delete(eventKey);
        return next;
      });
      Alert.alert(t('common.error'), t('calendar.hideExternalEventFailed'));
    }
  }

  function renderExternalEvent(event: ExternalDeviceCalendarEvent) {
    return (
      <View style={styles.externalRow}>
        <View
          style={[
            styles.externalColor,
            { backgroundColor: event.calendarColor || colors.gray400 },
          ]}
        />
        <View style={styles.rowText}>
          <Text style={styles.rowName}>
            {event.title || t('calendar.untitledExternalEvent')}
          </Text>
          <Text style={styles.rowWhen}>{externalWhen(event)}</Text>
          {!!event.location && <Text style={styles.rowLocation}>{event.location}</Text>}
          <Text style={styles.externalSource}>
            {event.calendarTitle || t('calendar.deviceCalendar')}
            {event.recurring ? ` · ${t('calendar.recurring')}` : ''}
            {` · ${externalAvailability(event)}`}
          </Text>
        </View>
        <TouchableOpacity
          style={styles.externalHideButton}
          accessibilityRole="button"
          accessibilityLabel={t('calendar.hideExternalEvent', {
            title: event.title || t('calendar.untitledExternalEvent'),
          })}
          hitSlop={4}
          onPress={() => hideExternalEvent(event)}>
          <Ionicons name="close" size={18} color={colors.gray400} />
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <View style={styles.topBar}>
        <NotificationBellButton onPress={onNotificationCenter} />
      </View>
      <View style={styles.segmentRow}>
        <TouchableOpacity
          style={[styles.segmentBtn, viewMode !== 'grid' && styles.segmentBtnActive]}
          accessibilityRole="tab"
          accessibilityState={{ selected: viewMode !== 'grid' }}
          onPress={() => selectViewMode('requests')}>
          <Text
            style={[styles.segmentText, viewMode !== 'grid' && styles.segmentTextActive]}
            numberOfLines={2}>
            {t('calendar.tabRequests')}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.segmentBtn, viewMode === 'grid' && styles.segmentBtnActive]}
          accessibilityRole="tab"
          accessibilityState={{ selected: viewMode === 'grid' }}
          onPress={() => selectViewMode('grid')}>
          <Text
            style={[styles.segmentText, viewMode === 'grid' && styles.segmentTextActive]}
            numberOfLines={2}>
            {t('calendar.tabCalendar')}
          </Text>
        </TouchableOpacity>
      </View>

      {viewMode !== 'grid' && (
        <View style={styles.requestSegmentRow}>
          <TouchableOpacity
            style={[
              styles.requestSegmentBtn,
              viewMode === 'requests' && styles.requestSegmentBtnActive,
            ]}
            accessibilityRole="tab"
            accessibilityState={{ selected: viewMode === 'requests' }}
            onPress={() => selectViewMode('requests')}>
            <Text style={[
              styles.requestSegmentText,
              viewMode === 'requests' && styles.requestSegmentTextActive,
            ]}>
              {t('calendar.tabActive')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.requestSegmentBtn,
              viewMode === 'history' && styles.requestSegmentBtnActive,
            ]}
            accessibilityRole="tab"
            accessibilityState={{ selected: viewMode === 'history' }}
            onPress={() => selectViewMode('history')}>
            <Text style={[
              styles.requestSegmentText,
              viewMode === 'history' && styles.requestSegmentTextActive,
            ]}>
              {t('calendar.tabHistory')}
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator color={colors.black} />
        </View>
      ) : failed ? (
        <View style={styles.center}>
          <Text style={styles.errorText}>{t('calendar.failed')}</Text>
          <CompactActionButton
            variant="primary"
            label={t('common.retry')}
            onPress={() => { load(); }}
            style={styles.retryBtn}
            testID="calendar-list-retry"
          />
        </View>
      ) : viewMode === 'requests' ? (
        <FlatList
          data={sortedForRequests}
          keyExtractor={(item) => String(item.id)}
          contentContainerStyle={styles.list}
          renderItem={({ item }) => renderRow(item)}
          ItemSeparatorComponent={() => <View style={styles.separator} />}
          ListEmptyComponent={<Text style={styles.empty}>{t('calendar.empty')}</Text>}
        />
      ) : viewMode === 'history' ? (
        <FlatList
          data={historyMeetups}
          keyExtractor={(item) => String(item.id)}
          contentContainerStyle={styles.list}
          renderItem={({ item }) => renderRow(item, false)}
          ItemSeparatorComponent={() => <View style={styles.separator} />}
          ListEmptyComponent={historyLoading ? (
            <ActivityIndicator color={colors.black} />
          ) : historyFailed ? (
            <CompactActionButton
              variant="primary"
              label={t('common.retry')}
              onPress={() => { loadHistory(undefined, true); }}
              style={styles.retryBtn}
              testID="calendar-history-retry"
            />
          ) : <Text style={styles.empty}>{t('calendar.empty')}</Text>}
          ListFooterComponent={historyMeetups.length > 0 ? (
            historyLoading ? <ActivityIndicator color={colors.black} />
              : historyCursor ? (
                <TouchableOpacity
                  style={styles.loadMoreButton}
                  accessibilityRole="button"
                  onPress={() => { loadHistory(historyCursor); }}>
                  <Text style={styles.loadMoreText}>{t('calendar.loadMore')}</Text>
                </TouchableOpacity>
              ) : null
          ) : null}
        />
      ) : (
        <View style={styles.gridWrap}>
          <View style={styles.monthNav}>
            <TouchableOpacity
              style={styles.monthNavButton}
              onPress={() => goWeek(-1)}
              accessibilityRole="button"
              accessibilityLabel={t('calendar.previousWeek')}>
              <Text style={styles.monthNavArrow}>‹</Text>
            </TouchableOpacity>
            <Text style={styles.monthNavTitle}>
              {selectedDate.toLocaleDateString(lang, { month: 'long', year: 'numeric' })}
            </Text>
            <TouchableOpacity
              style={styles.monthNavButton}
              onPress={() => goWeek(1)}
              accessibilityRole="button"
              accessibilityLabel={t('calendar.nextWeek')}>
              <Text style={styles.monthNavArrow}>›</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.weekdayRow}>
            {weekdayLabels.map((w, i) => (
              <Text key={i} style={styles.weekdayLabel}>{w}</Text>
            ))}
          </View>

          <View style={styles.weekRow}>
            {weekDays.map((d) => {
              const dayMeetups = meetupsByDate.get(dateKey(d)) ?? [];
              const hasExternalEvent = visibleExternalEvents.some((event) => eventOverlapsDay(event, d));
              const isToday = sameDay(d, new Date());
              const isSelected = sameDay(d, selectedDate);
              return (
                <TouchableOpacity
                  key={dateKey(d)}
                  style={[styles.dayCell, isSelected && styles.dayCellSelected]}
                  accessibilityRole="button"
                  accessibilityLabel={d.toLocaleDateString(lang, {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                  accessibilityState={{ selected: isSelected }}
                  onPress={() => setSelectedDate(d)}>
                  <Text style={[
                    styles.dayNumber,
                    isToday && styles.dayNumberToday,
                    isSelected && styles.dayNumberSelected,
                  ]}>
                    {formatDisplayCount(d.getDate(), lang)}
                  </Text>
                  {(dayMeetups.length > 0 || hasExternalEvent) && (
                    <View style={[styles.dayDot, isSelected && styles.dayDotSelected]} />
                  )}
                </TouchableOpacity>
              );
            })}
          </View>

          <View style={styles.separator} />

          {externalLoading && (
            <ActivityIndicator style={styles.externalLoader} color={colors.black} />
          )}
          {externalFailed && (
            <TouchableOpacity
              style={styles.externalError}
              onPress={() => setExternalReloadKey((key) => key + 1)}>
              <Text style={styles.externalErrorText}>{t('calendar.externalEventsFailed')}</Text>
              <Text style={styles.externalRetry}>{t('common.retry')}</Text>
            </TouchableOpacity>
          )}

          <FlatList
            data={agendaItems}
            keyExtractor={(item) => item.key}
            contentContainerStyle={styles.list}
            renderItem={({ item }) => item.type === 'meetup'
              ? renderRow(item.meetup)
              : renderExternalEvent(item.event)}
            ItemSeparatorComponent={() => <View style={styles.separator} />}
            ListEmptyComponent={<Text style={styles.empty}>{t('calendar.noEventsForDay')}</Text>}
          />
        </View>
      )}
    </View>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.white },
  header: { paddingHorizontal: spacing.lg, paddingVertical: spacing.md },
  topBar: {
    minHeight: 52,
    paddingHorizontal: spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: spacing.xs,
  },
  title: { fontSize: fontSize.lg, fontWeight: '700', color: colors.black },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  errorText: { fontSize: fontSize.md, color: colors.gray400, marginBottom: spacing.md },
  retryBtn: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: radius.control,
    backgroundColor: colors.black,
  },
  retryText: { color: colors.white, fontSize: fontSize.md, fontWeight: '600' },
  loadMoreButton: {
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: spacing.md,
  },
  loadMoreText: { color: colors.black, fontSize: fontSize.md, fontWeight: '600' },
  segmentRow: {
    flexDirection: 'row',
    marginTop: spacing.md,
    marginHorizontal: spacing.lg,
    marginBottom: spacing.md,
    backgroundColor: colors.gray100,
    borderRadius: radius.control,
    padding: 4,
    gap: 4,
  },
  segmentBtn: { flex: 1, minHeight: 44, paddingVertical: spacing.sm, borderRadius: radius.control, alignItems: 'center', justifyContent: 'center' },
  segmentBtnActive: { backgroundColor: colors.black },
  segmentText: { fontSize: fontSize.md, fontWeight: '600', color: colors.gray600, textAlign: 'center' },
  segmentTextActive: { color: colors.white },
  requestSegmentRow: {
    flexDirection: 'row',
    alignSelf: 'center',
    marginHorizontal: spacing.lg,
    marginBottom: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray200,
  },
  // D-412 — segment sekmesi 40 px'ti. Yatay `hitSlop` komşu sekmenin alanına
  // taşıp yanlış basış üretirdi; bu yüzden çubuk BÜYÜTÜLEREK hedefe çıkıldı.
  requestSegmentBtn: {
    minWidth: 112,
    minHeight: MIN_TOUCH_TARGET,
    paddingHorizontal: spacing.lg,
    alignItems: 'center',
    justifyContent: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  requestSegmentBtnActive: { borderBottomColor: colors.black },
  requestSegmentText: {
    fontSize: fontSize.sm,
    fontWeight: '600',
    color: colors.gray400,
    textAlign: 'center',
  },
  requestSegmentTextActive: { color: colors.black },
  list: { paddingHorizontal: spacing.lg },
  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.md, gap: spacing.sm },
  rowLink: { flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  rowClosed: { opacity: 0.45 },
  avatar: { marginRight: spacing.xs },
  rowText: { flex: 1, minWidth: 0 },
  rowName: { fontSize: fontSize.md, fontWeight: '600', color: colors.black },
  rowKind: { fontSize: fontSize.sm, fontWeight: '600', color: colors.gray600 },
  rowWhen: { fontSize: fontSize.sm, color: colors.gray600, marginTop: 2 },
  rowLocation: { fontSize: fontSize.sm, color: colors.gray400, marginTop: 2 },
  rowStatus: { fontSize: fontSize.sm, color: colors.gray400, marginTop: 2 },
  calendarCopyWarning: { fontSize: fontSize.sm, color: colors.gray600, marginTop: 3 },
  externalRow: { flexDirection: 'row', alignItems: 'stretch', paddingVertical: spacing.md, gap: spacing.sm },
  externalColor: { width: 4, borderRadius: 2 },
  externalSource: { fontSize: fontSize.sm, color: colors.gray400, marginTop: 3 },
  externalHideButton: {
    width: 40,
    height: 40,
    marginTop: -spacing.xs,
    marginRight: -spacing.xs,
    alignItems: 'center',
    justifyContent: 'center',
  },
  externalLoader: { marginVertical: spacing.sm },
  externalError: { alignItems: 'center', paddingVertical: spacing.sm },
  externalErrorText: { color: colors.gray400, fontSize: fontSize.sm },
  externalRetry: { color: colors.black, fontSize: fontSize.sm, fontWeight: '600', marginTop: 2 },
  actions: { width: 104, flexShrink: 0, gap: spacing.xs },
  // D-411 — geometri ve 44 pt dokunma hedefi `CompactActionButton`'da; burada
  // yalnız satırın ayırdığı genişlik kalır. Eski 40 px yükseklik değişti.
  rowAction: { width: 104 },
  declineBtn: { borderWidth: 1, borderColor: colors.gray200 },
  declineBtnText: { color: colors.gray600, fontSize: fontSize.sm, fontWeight: '600', textAlign: 'center' },
  separator: { height: 1, backgroundColor: colors.gray100 },
  empty: { fontSize: fontSize.md, color: colors.gray400, textAlign: 'center', paddingTop: spacing.xl },
  gridWrap: { flex: 1 },
  monthNav: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    marginBottom: spacing.sm,
  },
  monthNavButton: { width: 44, height: 44, alignItems: 'center', justifyContent: 'center' },
  monthNavArrow: { fontSize: fontSize.xl, color: colors.gray600 },
  monthNavTitle: { flex: 1, minWidth: 0, textAlign: 'center', fontSize: fontSize.md, fontWeight: '600', color: colors.black, textTransform: 'capitalize' },
  weekdayRow: { flexDirection: 'row', paddingHorizontal: spacing.lg, marginBottom: spacing.xs },
  weekdayLabel: {
    flex: 1,
    textAlign: 'center',
    fontSize: fontSize.sm,
    color: colors.gray400,
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  weekRow: { flexDirection: 'row', paddingHorizontal: spacing.lg },
  dayCell: {
    width: `${100 / 7}%`,
    height: 46,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radius.control,
  },
  dayCellSelected: { backgroundColor: colors.black },
  dayNumber: { fontSize: fontSize.sm, color: colors.black },
  dayNumberToday: { fontWeight: '700' },
  dayNumberSelected: { color: colors.white, fontWeight: '700' },
  dayDot: { width: 4, height: 4, borderRadius: 2, backgroundColor: colors.black, marginTop: 2 },
  dayDotSelected: { backgroundColor: colors.white },
});
